import { useEffect, useState, useCallback } from 'react';
import GenericDialog from '../../../components/Dialog/GenericDialog';
import { createGuid } from '../../../components/Utilities/HelperMethods';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AgencyManageFileSave } from '../../../api/SecureFileUploader';
import { useDeleteCanceledFiles } from '../Hooks/useDeleteCanceledFiles';
import { Snackbar } from '../../../components/Global/Snackbar/Snackbar';
import SecureFileUploaderRecordModalContent from './SecureFileUploaderRecordModalContent';

const SecureFileUploaderRecordModal = ({ isModalOpen, toggleModal, recordID }) => {
  const queryClient = useQueryClient();
  const [submitForm, setSubmitForm] = useState(null);
  const [sessionGuid, setSessionGuid] = useState(null);
  const [hasFiles, setHasFiles] = useState(false);
  const [fileOpsInProgress, setFileOpsInProgress] = useState(false);
  const [snackbarDetails, setSnackbarDetails] = useState({
    isOpen: false,
    message: '',
    status: '',
    requiresAction: false,
  });

  const deleteCanceledFilesMutation = useDeleteCanceledFiles(
    (message, res) => {
      setSnackbarDetails({
        isOpen: true,
        message: message,
        status: 'success',
      });
    },
    (errorMessage, error) => {
      setSnackbarDetails({
        isOpen: true,
        message: errorMessage,
        status: 'error',
      });
    }
  );

  const agencyManageFileSaveMutation = useMutation({
    mutationFn: (formData) => AgencyManageFileSave(formData)(),
    onSuccess: (res) => {
      const message = res?.Message || res?.message || 'Files uploaded successfully!';

      setSnackbarDetails({
        isOpen: true,
        message: message,
        status: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['GetFilesUploadedByAgencyAndStatus'],
      });
      toggleModal(false);
    },
    onError: async (error) => {
      agencyManageFileSaveMutation.reset();

      const errorMessage = error.message || 'An error occurred while uploading files';

      await deleteCanceledFilesMutation.mutateAsync(sessionGuid);

      toggleModal(false);

      setSnackbarDetails({
        isOpen: true,
        message: errorMessage,
        status: 'error',
        requiresAction: true,
      });
    },
  });

  // grab isMutating state to pass to modal to set inputs to disabled when these are fetching
  const isMutating =
    agencyManageFileSaveMutation.isLoading || deleteCanceledFilesMutation.isLoading;

  // create guid token on modal open, simple state
  useEffect(() => {
    setSessionGuid(createGuid());
    setHasFiles(false);
  }, [isModalOpen]);

  const handleFormSubmit = useCallback(
    (formData) => {
      const { files, ...submitData } = formData;

      agencyManageFileSaveMutation.mutate({
        ...submitData,
      });
    },
    [agencyManageFileSaveMutation]
  );

  const handleConfirm = () => {
    if (submitForm) {
      submitForm();
    }
  };

  const handleCancel = async () => {
    if (hasFiles) {
      await deleteCanceledFilesMutation.mutateAsync(sessionGuid);
    }
    setSessionGuid(null);
    setHasFiles(false);
    toggleModal(false);
  };

  return (
    <>
      <GenericDialog
        open={isModalOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleCancel();
        }}
        showCloseIcon={false}
        title={'Manage Record'}
        fullWidth={true}
        maxWidth={'sm'}
        onConfirm={handleConfirm}
        cancelLoading={deleteCanceledFilesMutation.isLoading}
        confirmLoading={agencyManageFileSaveMutation.isLoading}
        disableConfirm={fileOpsInProgress}
        disableCancel={fileOpsInProgress}
        content={
          <SecureFileUploaderRecordModalContent
            handleCancel={handleCancel}
            sessionGuid={sessionGuid}
            setFileOpsStatus={setFileOpsInProgress}
            isDisabled={isMutating}
            setHasFiles={setHasFiles}
            setSubmitForm={setSubmitForm}
            onSubmit={handleFormSubmit}
            recordID={recordID}
          />
        }
      />
      <Snackbar
        open={snackbarDetails.isOpen}
        message={snackbarDetails.message}
        status={snackbarDetails.status}
        requiresAction={snackbarDetails.requiresAction}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() =>
          setSnackbarDetails((prev) => ({
            ...prev,
            isOpen: false,
            requiresAction: false,
          }))
        }
      />
    </>
  );
};

export default SecureFileUploaderRecordModal;
